import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ProgressWheel } from '../../../Tools/LoadingIndicator';
import { uploadFileToFirebase } from '../../../../utils/firebase';
import { useWorkspace } from '../../../../context/WorkspaceContext';
import { getFileType } from '../../../../utils/getFileType';
import { MdDelete, MdCloudUpload } from 'react-icons/md';
import { FaImage, FaVideo } from 'react-icons/fa';

function MultipleUploadArea({ onFileSelect, uploadType, onUploadStart, onUploadComplete }) {
    const fileInputRef = useRef(null);
    const maxSizeBytes = 2 * 1024 * 1024 * 1024; // 2 GB
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(new Set());
    const [uploadProgress, setUploadProgress] = useState({});
    const { activeWorkspace } = useWorkspace();

    // Define supported types based on uploadType
    const videoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv'];
    const imageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];

    // Determine the supported types and accept attribute
    let supportedTypes = [];
    let acceptTypes = '';
    let allowedFormatsText = '';
    if (uploadType === 'video') {
        supportedTypes = videoTypes;
        acceptTypes = 'video/*';
        allowedFormatsText = 'Allowed formats: .mp4, .avi, .mov, .wmv (max size 2GB)';
    } else if (uploadType === 'post') {
        supportedTypes = imageTypes;
        acceptTypes = 'image/*';
        allowedFormatsText = 'Allowed formats: .jpg, .jpeg, .png (max size 2GB)';
    } else {
        // Default to all types if uploadType is not specified
        supportedTypes = [...videoTypes, ...imageTypes];
        acceptTypes = 'video/*,image/*';
        allowedFormatsText = 'Allowed formats: .mp4, .avi, .mov, .wmv, .jpg, .jpeg, .png (max size 2GB)';
    }

    // Validate and process files
    const validateAndAddFiles = async (files) => {
        const validFiles = [];
        const fileArray = Array.from(files);

        fileArray.forEach((file) => {
            // Check if the file type is supported
            if (!supportedTypes.includes(file.type)) {
                toast.error(`Unsupported file type: ${file.name}`);
                return;
            }

            // Check if the file size exceeds the maximum allowed size
            if (file.size > maxSizeBytes) {
                toast.error(`File size exceeds 2GB limit: ${file.name}`);
                return;
            }

            // Check if file already exists
            const isDuplicate = selectedFiles.some(selectedFile => 
                selectedFile.name === file.name && selectedFile.size === file.size
            );

            if (isDuplicate) {
                toast.warning(`File already selected: ${file.name}`);
                return;
            }

            validFiles.push({
                id: Date.now() + Math.random(), // Simple unique ID
                file: file,
                name: file.name,
                size: file.size,
                type: getFileType(file.name),
                preview: URL.createObjectURL(file),
                status: 'uploading' // Start as uploading since we'll upload immediately
            });
        });

        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
            
            // Start all uploads simultaneously
            const uploadPromises = validFiles.map(fileData => uploadSingleFile(fileData));
            
            // Wait for all uploads to complete (but they run in parallel)
            try {
                await Promise.allSettled(uploadPromises);
            } catch (error) {
                console.error('Some uploads failed:', error);
            }
        }
    };

    // Handle file input change (when files are selected via the file input)
    const handleFileChange = async (e) => {
        e.preventDefault();
        const files = e.target.files;
        if (files && files.length > 0) {
            await validateAndAddFiles(files);
        }
        // Reset the input value to allow selecting the same file again
        e.target.value = '';
    };

    // Handle button click to open the file input dialog
    const handleSelectButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle file drop event (when files are dropped onto the upload area)
    const handleDrop = async (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            await validateAndAddFiles(files);
        }
    };

    // Prevent default behavior when dragging files over the upload area
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    // Remove a file from the selection
    const removeFile = (fileId) => {
        setSelectedFiles(prev => {
            const fileToRemove = prev.find(f => f.id === fileId);
            if (fileToRemove && fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            return prev.filter(f => f.id !== fileId);
        });
    };

    // Upload a single file to Firebase
    const uploadSingleFile = async (fileData) => {
        const { id, file, type } = fileData;
        let uploadTrackingId = null;
        
        try {
            setUploadingFiles(prev => new Set([...prev, id]));
            setSelectedFiles(prev => prev.map(f => 
                f.id === id ? { ...f, status: 'uploading' } : f
            ));

            // Track upload start in parent component
            if (onUploadStart) {
                uploadTrackingId = onUploadStart(fileData.name);
            }

            let fileType = 'video';
            if (['image', 'img', 'post'].includes(type)) {
                fileType = 'img';
            }

            const progressCallback = (progress) => {
                setUploadProgress(prev => ({ ...prev, [id]: progress }));
            };

            const downloadUrl = await uploadFileToFirebase(file, fileType, progressCallback, activeWorkspace.id);
            
            // Call the callback with the Firebase URL
            onFileSelect(downloadUrl, null);
            
            toast.success(`Uploaded: ${fileData.name}`);
            
            // Clean up preview URL and remove the file from the list after successful upload
            if (fileData.preview) {
                URL.revokeObjectURL(fileData.preview);
            }
            setSelectedFiles(prev => prev.filter(f => f.id !== id));
            
        } catch (error) {
            console.error(`Failed to upload file ${fileData.name}:`, error);
            setSelectedFiles(prev => prev.map(f => 
                f.id === id ? { ...f, status: 'error' } : f
            ));
            toast.error(`Failed to upload: ${fileData.name}`);
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
            setUploadProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[id];
                return newProgress;
            });

            // Track upload completion in parent component
            if (onUploadComplete && uploadTrackingId) {
                onUploadComplete(uploadTrackingId);
            }
        }
    };



    // Clear all files (for external use)
    const clearAllFiles = () => {
        selectedFiles.forEach(file => {
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }
        });
        setSelectedFiles([]);
        setUploadProgress({});
        setUploadingFiles(new Set());
    };

    // Get file icon
    const getFileIcon = (type) => {
        switch (type) {
            case 'image': return <FaImage className="text-blue-500" size={16} />;
            case 'video': return <FaVideo className="text-red-500" size={16} />;
            default: return <FaImage className="text-gray-500" size={16} />;
        }
    };

    // Cleanup URLs on unmount
    useEffect(() => {
        return () => {
            selectedFiles.forEach(file => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, []);

    const isUploading = uploadingFiles.size > 0;

    return (
        <div className="min-w-full h-full --theme-colors">
            {/* Upload Area */}
            <div
                className="upload-area h-full cursor-pointer border-2 border-dashed border-gray-300 rounded-lg transition-colors hover:border-gray-400 hover:bg-gray-50/50"
                onClick={handleSelectButtonClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                <div className="flex flex-col items-center justify-center space-y-4 p-8 h-full">
                    <MdCloudUpload className="text-gray-400" size={48} />
                    <div className="text-center">
                        <p className="font-semibold text-lg text-gray-700">
                            Upload multiple files
                        </p>
                        <p className="text-sm text-gray-500">
                            Drag & Drop files here or click to browse
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                            {allowedFormatsText}
                        </p>
                    </div>
                </div>

                {/* Hidden file input for selecting files */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptTypes}
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
                <div className="mt-4 max-h-64 overflow-y-auto border rounded-lg bg-gray-50 !text-black">
                    <div className="p-3">
                        <h4 className="font-medium text-sm mb-3">Selected Files:</h4>
                        <div className="space-y-2">
                            {selectedFiles.map((fileData) => (
                                <div
                                    key={fileData.id}
                                    className={`flex items-center justify-between p-2 rounded border bg-white ${
                                        fileData.status === 'error' ? 'border-red-200 bg-red-50' :
                                        fileData.status === 'uploading' ? 'border-blue-200 bg-blue-50' :
                                        'border-gray-200'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {getFileIcon(fileData.type)}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{fileData.name}</p>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500">
                                                    {(fileData.size / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                                <span className={`text-xs px-2 py-1 rounded ${
                                                    fileData.status === 'error' ? 'bg-red-100 text-red-800' :
                                                    fileData.status === 'uploading' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {fileData.status === 'error' ? 'Failed' :
                                                     fileData.status === 'uploading' ? 'Uploading...' :
                                                     'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress indicator */}
                                    <div className="flex items-center gap-2 ml-2">
                                        {fileData.status === 'uploading' && uploadProgress[fileData.id] && (
                                            <div className="w-8 h-8">
                                                <ProgressWheel 
                                                    progress={uploadProgress[fileData.id]} 
                                                    size={32} 
                                                />
                                            </div>
                                        )}

                                        {fileData.status !== 'uploading' && (
                                            <button
                                                onClick={() => removeFile(fileData.id)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                            >
                                                <MdDelete size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MultipleUploadArea; 